import React, { useEffect } from 'react';

import { isEmpty } from '@groww-tech-pvt/ella';
import { Button, VARIANTS, BUTTON_SIZES } from '@groww-tech-pvt/ui-toolkit';
import { getIsDomain915 } from 'src/utils/config/domainConfig';

import styles from './actionFooter.module.css';

interface ButtonConfig {
  text: string;
  className?: string;
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

interface ActionFooterProps {
  errorMessage?: string;
  infoMessage?: React.ReactNode;
  primaryButton?: ButtonConfig;
  secondaryButton?: ButtonConfig;
  children?: React.ReactNode;
}

const ActionFooter: React.FC<ActionFooterProps> = ({
  errorMessage,
  infoMessage,
  primaryButton,
  secondaryButton,
  children,
}) => {
  const hasButtons = primaryButton || secondaryButton;
  const isDomain915 = getIsDomain915();
  if (!hasButtons && !errorMessage && !infoMessage) {
    return null;
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && primaryButton?.onClick && !primaryButton?.isDisabled) {
      e.preventDefault();
      (primaryButton?.onClick)();
    }
  };

  useEffect(() => {
    if (primaryButton?.onClick) {
      document.addEventListener('keydown', handleKeyDown as unknown as EventListener);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown as unknown as EventListener);
    };
  }, [primaryButton?.onClick]);

  return (
    <div className='flex flex-col gap-1.5 w-full sticky bottom-0'>
      {!isEmpty(errorMessage) && (
        <div className='contentNegative bodyBase text-center'>{errorMessage}</div>
      )}

      {!isEmpty(infoMessage) && (
        <div className='bodySmall center-align'>
          <p className='rounded-lg backgroundSecondary p-1'>{infoMessage}</p>
        </div>
      )}

      {children}

      {hasButtons && (
        <div className='flex sm:flex-row sm:gap-2.5 gap-2 w-full'>
          {secondaryButton && (
            <div className='flex-1'>
              <Button
                buttonText={secondaryButton.text}
                dataTestId='secondary-action-btn'
                gradientClass={secondaryButton.className ?? 'flex-1 bg-primary!'}
                onClick={secondaryButton.onClick}
                variant={VARIANTS.SECONDARY}
                size={BUTTON_SIZES.BASE}
                isAccent={true}
                isLoading={secondaryButton.isLoading}
                isDisabled={secondaryButton.isDisabled}
                isFullWidth={true}
              />
            </div>
          )}

          {primaryButton && (
            <div className={`flex-1 ${(isDomain915 && !primaryButton.isLoading && !primaryButton.isDisabled) ? styles.button915 : ''}`}>
              <Button
                buttonText={primaryButton.text}
                dataTestId='primary-action-btn'
                className={primaryButton.className ?? 'flex-1'}
                onClick={primaryButton.onClick}
                variant={VARIANTS.PRIMARY}
                size={BUTTON_SIZES.BASE}
                isLoading={primaryButton.isLoading}
                isDisabled={primaryButton.isDisabled}
                isFullWidth
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionFooter;
